import Logger from '@/utils/Logger';
import Region, { getType as getRegionType } from '@/utils/Region';

export default class ContextInfo {

  private _id: string;
  private _contextPath = '';
  private _baseUrl = '';
  private _requestUrl = '';
  private _requestUri = ''
  private _channelsInfoWorkspace = '';
  private _channelsInfoNodePath = '';
  private _channelsInfoPropName = '';
  private _useLocalizedChannelNames = true;
  private _isAuthoring = false;
  private _videoListUsePagination = false;

  private _localeDefault = 'en';
  private _country: string | undefined;

  private _locale = this._localeDefault;
  private _localesSupported = [this._locale];
  private _region: Region | undefined = undefined;

  private _texts: Map<string, string> = new Map();

  private _needsCookieConsent = false;

  private readonly textPrefix = 'data-text-';
  private readonly textPrefixLen = this.textPrefix.length;

  constructor(id: string) {
    this._id = id;
  }

  public getText(key: string) {
    return this._texts.has(key)
      ? this._texts.get(key)
      : key;
  }

  public readFrom(attributes: Record<string, string>) {

    for(const key in attributes) {
      if(key === 'data-context-path') {
        this.contextPath = attributes[key];
      }
      else if(key === 'data-base-url') {
        this._baseUrl = attributes[key];
      }
      else if(key === 'data-request-uri') {
        this._requestUri = attributes[key];
      }
      else if(key === 'data-request-url') {
        this._requestUrl = attributes[key];
      }
      else if(key === 'data-channels-info-workspace') {
        this._channelsInfoWorkspace = attributes[key];
      }
      else if(key === 'data-channels-info-node-path') {
        this._channelsInfoNodePath = attributes[key];
      }
      else if(key === 'data-channels-info-prop-name') {
        this._channelsInfoPropName = attributes[key];
      }
      else if(key === 'data-current-locale') {
        this._locale = attributes[key];
        this._locale = this._locale.replace('_', '-');
      }
      else if(key === 'data-supported-locales') {
        this._localesSupported = attributes[key].replace('_', '-').split(' ');
        if(this._localesSupported.indexOf(this._localeDefault) === -1) {
          this._localesSupported.push(this._localeDefault);
        }
      }
      else if(key === 'data-country-info') {
        this.country = attributes[key];
      }
      else if(key === 'data-region-info') {
        try {
          getRegionType(attributes[key]);
        }
        catch(error) {
          Logger.warn(`Region ${attributes[key]} is not supported.`);
        }
      }
      else if(key === 'data-needs-cookie-consent' && attributes[key] === 'true') {
        this._needsCookieConsent = true;
      }
      else if(key === 'data-localized-channels' && attributes[key] === 'false') {
        this._useLocalizedChannelNames = false;
      }
      else if(key === 'data-is-authoring' && attributes[key] === 'true') {
        this._isAuthoring = true;
      }
      else if(key === 'data-videolist-use-pagination' && attributes[key] === 'true') {
        this._videoListUsePagination = true;
      }
      else if(key.startsWith(this.textPrefix)) {
        this._texts.set(key.substr(this.textPrefixLen), attributes[key]);
      }
    }
  }

  public setRegionByCountryIf(country?: string) {
    if(this.region !== undefined) return;
    this.setRegionByCountry(country);
  }

  public setRegionByCountry(country?: string) {
    if(!country) country = this._country;
    if(!country) return;

    switch(country) {
      case 'com.cn':
      case 'com-cn':
      case 'cn':
      case 'com.hk':
      case 'com-hk':
      case 'hk':
      case 'com.au':
      case 'com-au':
      case 'au':
      case 'com.my':
      case 'com-my':
      case 'my':
      case 'in':
        this._region = Region.AP;
        break;
      case 'com.br':
      case 'com-br':
      case 'br':
      case 'ca':
      case 'us':
        this._region = Region.AM;
        break;
      case 'com':
        this._region = Region.GOBAL;
        break;
      default:
        this._region = Region.EU;
    }
  }

  public getCountryCode(): string|undefined {

    if(this.country && this.country.length >= 2) {
      if(this.country.length === 2) {
        return this.country;
      }
      if(this.country.endsWith('com.cn')) return 'cn';
      if(this.country.endsWith('com.hk')) return 'hk';
      if(this.country.endsWith('com.au')) return 'au';
      if(this.country.endsWith('com.my')) return 'my';
      if(this.country.endsWith('com.br')) return 'br';


      return this.country;
    }
    return undefined;
  }

  public get id() {
    return this._id;
  }
  public get contextPath() {
    return this._contextPath;
  }
  public set contextPath(value) {
    this._contextPath = value;
  }
  public get region(): Region | undefined {
    return this._region;
  }
  public set region(value: Region | undefined) {
    this._region = value;
  }
  public get country(): string | undefined {
    return this._country;
  }
  public set country(value: string | undefined) {
    this._country = value?.replace('www.belimo.', '').replace('belimo', '');
  }
  public get requestUrl() {
    return this._requestUrl;
  }
  public get requestUri() {
    return this._requestUri;
  }
  public get baseUrl() {
    return this._baseUrl;
  }
  public get texts() {
    return this._texts;
  }
  public get locale() {
    return this._locale;
  }
  public get needsCookieConsent() {
    return this._needsCookieConsent;
  }
  public get channelsInfoWorkspace() {
    return this._channelsInfoWorkspace;
  }
  public get channelsInfoJcrPropertyPath() {
    return this._channelsInfoNodePath + '/' + this._channelsInfoPropName;
  }
  public get useLocalizedChannelNames() {
    return this._useLocalizedChannelNames;
  }
  public get isAuthoring() {
    return this._isAuthoring;
  }
  public get videoListUsePagination() {
    return this._videoListUsePagination;
  }
}
